import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { useFlag } from '@unleash/proxy-client-react';
import { isAwaitingReviewOnly } from '../../../utils/datasetReviewState';
import { mergeFileStatuses } from '../../../utils/fileReviewStatus';
import { canRetryFile, retryStatusText, rejectionText, UPLOAD_REJECTED_RETRY } from '../../../utils/retryReasons';
import { requireVerb } from '../../../utils/reviewRequiredNotice';
import { apiService } from '../../../services/api';
import { jobStatusToLabel } from '../../../utils/datasetStatusLabel';
import ReviewMediaPanel from './ReviewMediaPanel';
import RowActionsMenu from '../shared/RowActionsMenu';
import DatasetMenuItems, { canShowDatasetMenu } from './DatasetMenuItems';
import Timestamp from '../shared/Timestamp';
import './DatasetDetails.css';

/**
 * View Component for Dataset Details
 */
// Bridge UI: the icons beside a file name carry no status colour of their own —
// they recede to icon-grey and let the count line and chip carry the meaning.
// icon-grey per design review on the PR (the written feedback said icon-black;
// the designer corrected it to grey, which supersedes the doc).
//
// `failed` is the exception: that red IS the signal, so it keeps a colour — now
// the brand error token rather than the off-palette red it used to hardcode.
//
// Outlined glyphs, per Figma 1017-2637 — the frame draws check_circle_outline
// and error_outline, not their filled counterparts. Separate axis from the
// colour correction on PR #178, which was about black vs grey.
//
// NOTE — there is deliberately NO `awaiting_review` row here.
//
// A file parked for document field review must not read as "still working".
// The files endpoint still reports a parked file as `uploaded` (verified
// against the running backend, PRD-1358), but /jobs carries `awaiting_review`
// per file, and `allFiles` below merges the two (mergeFileStatuses) — so the
// `awaiting_review` row here now fires, where an earlier attempt could not.
// The ResAI "Queued" label is fixed the same way.
const ICON_GREY = 'var(--color-icon-grey)';
// Glyphs per Figma 1017-2637's File Status rows: a check for a finished file,
// an exclamation circle for one parked for review. Drawn from the OUTLINED
// icon font, so every status reads as an outline. All grey, 16px.
const STATUS_ICON = {
  success: { icon: 'check_circle', color: ICON_GREY },
  failed: { icon: 'error', color: 'var(--color-icon-error)' },
  // PRD-1628: refused at upload. Same red as failed, never the processing glyph.
  rejected: { icon: 'error', color: 'var(--color-icon-error)' },
  processing: { icon: 'hourglass_top', color: ICON_GREY },
  complete: { icon: 'check_circle', color: ICON_GREY },
  // `error_outline` even in the outlined font — that font's `error` is a filled disc.
  awaiting_review: { icon: 'error_outline', color: ICON_GREY },
  // CSV review. There is no icon-warn token — the only warn token is a SURFACE
  // colour — so rather than keep hardcoding the old amber or press a surface
  // token into service as a foreground, this joins the greys. Consistent with
  // the same review that greyed the rest of this panel.
  awaiting_input: { icon: 'rate_review', color: ICON_GREY },
  queued: { icon: 'schedule', color: ICON_GREY },
};

// A file the user has to act on: failed in the pipeline, or rejected at upload
// (PRD-1628). Rejected files sit with failed ones in the attention box.
const needsAction = (file) => file.status === 'failed' || file.status === 'rejected';

const DatasetDetails = ({ dataset, onClose, enableCsvHitlReview, reviewSubmitted, onOpenReview, onOpenDocumentReview, features = {}, onEdit, onDelete, onToggleShare, onTogglePause, showAiIndexedSummary = false }) => {
  const reviewerMediaEnabled = useFlag('mobile_reviewer_media');
  // PRD-1544: retry failed files. Owner-only (all dataset actions are owner-only
  // server-side) and flag-gated; with the flag off the panel is identical to today.
  const documentRetryEnabled = useFlag('document_retry');
  const canRetry = documentRetryEnabled && Boolean(features.can_edit_dataset) && dataset.is_owner !== false;
  // Optimistic state so a retried file (and its dataset) show Processing immediately,
  // with no reload — the real status then arrives through the existing polling.
  // file_id → attempts_used at submit; see isPendingRetry for when it stops applying.
  const [retryingIds, setRetryingIds] = useState(() => new Map());
  // Per-file rejections from the retry 202 (shown inline on the file, not as a toast).
  const [retryRejections, setRetryRejections] = useState({});
  const [retryError, setRetryError] = useState(null);
  const [sampleData, setSampleData] = useState(null);
  // Per-file extracted fields (keyed by file_id) for the reviewer media view (R5).
  const [resultsByFile, setResultsByFile] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const hasFetchedRef = useRef(false);
  const [pipelineJobs, setPipelineJobs] = useState(null);
  const [pipelineJobsLoading, setPipelineJobsLoading] = useState(false);
  const [fileStatusExpanded, setFileStatusExpanded] = useState(false);
  // Authoritative per-file list from /datasets/{id}/files — the complete set of
  // files the card's rollup counts are derived from. Preferred over /jobs, which
  // only carries files already dispatched into a pipeline job.
  const [datasetFileList, setDatasetFileList] = useState([]);

  const getStatusLabel = (status) => {
    return status === 'published' ? 'Published' : 'Draft';
  };

  // Shared with the Data Sources card so the pill label can't drift (PRD-1119).
  const getJobStatusLabel = (jobStatus) => jobStatusToLabel(jobStatus);

  const getDisplayStatus = (dataset) => {
    const job = dataset.job_status?.toLowerCase();
    if (job && !['complete', 'completed'].includes(job)) {
      return getJobStatusLabel(dataset.job_status);
    }
    // An all-parked job reports job_status 'complete' by design, so without this
    // the modal would read "Published" over a dataset still awaiting a human.
    // Mirrors DataSources.getDisplayStatus.
    // Same processing guard as DataSources — all review surfaces switch together.
    if (isAwaitingReviewOnly(dataset)) {
      return 'Review required';
    }
    if (!job && dataset.status === 'published') {
      return 'Processing';
    }
    return getStatusLabel(dataset.status);
  };

  // Status -> Figma chip variant (PRD-1361), in step with the Data Sources card.
  const getDisplayStatusVariant = (dataset) => {
    const job = dataset.job_status?.toLowerCase();
    if (job && !['complete', 'completed'].includes(job)) {
      if (job === 'failed' || job === 'error') return 'error';
      // awaiting_input reads "Review required" (jobStatusToLabel), so it wears the
      // Alert chip like the card does, not in-progress blue.
      if (job === 'awaiting_input') return 'alert';
      return 'processing';
    }
    // Review-required is its own tier, not a processing state. Falling through
    // to processing rendered this chip in-progress blue while the card rendered
    // the same state in Alert amber, so one dataset read as two different things
    // depending on where you looked.
    if (isAwaitingReviewOnly(dataset)) return 'alert';
    if (!job && dataset.status === 'published') return 'processing';
    return dataset.status === 'published' ? 'success' : 'standard';
  };

  // `quiet` skips the loading line, which replaces the whole File Status panel —
  // used after a retry so the optimistic "Retrying…" stays on screen.
  const fetchPipelineJobs = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setPipelineJobsLoading(true);
    try {
      // Load the authoritative per-file list (all files, real statuses) and the
      // pipeline jobs in parallel. The file list drives the File Status section
      // so its counts match the card; jobs remain a secondary fallback.
      const [jobs, filesResp] = await Promise.all([
        apiService.getDatasetJobs(dataset.id),
        apiService.getDatasetFiles(dataset.id).catch((fileErr) => {
          console.error('Failed to load dataset files:', fileErr);
          return null;
        }),
      ]);
      setPipelineJobs(jobs);
      if (filesResp) setDatasetFileList(filesResp.items || []);
    } catch (err) {
      console.error('Failed to load pipeline jobs:', err);
    } finally {
      if (!quiet) setPipelineJobsLoading(false);
    }
  }, [dataset.id]);

  // PRD-1359 (feedback doc §Processing a New Dataset): "if there are more than
  // one files which require review and I don't review them all only one of
  // them, the new count does not update under File Status".
  //
  // The cause: this fetch was keyed on `dataset.id` alone, so the File Status
  // panel loaded once and then never again while the modal stayed open on the
  // same dataset — a reviewer settling one of three parked documents kept
  // reading the old count. The Status chip above it did update, because
  // DataSources re-derives `selectedDataset` from every poll, which made the
  // two halves of the same modal disagree.
  //
  // Keyed on the rollup instead: the parent hands down a fresh record each poll,
  // so when a document is released the counts change and this refetches. A
  // string signature rather than the object, which is a new reference every poll
  // and would refetch forever.
  const fileCountsKey = JSON.stringify(dataset.file_counts || {});

  useEffect(() => {
    fetchPipelineJobs();
  }, [fetchPipelineJobs, fileCountsKey]);

  // Flatten all pipeline files into a single list, deduplicated by file_id.
  // When the same file is processed by multiple pipelines (e.g. unstructured + form_processor),
  // keep the worst status so failures are always visible.
  // The file list decides which files appear (the card counts come from it);
  // /jobs fills in the review state it does not carry. See mergeFileStatuses.
  const allFiles = useMemo(() => mergeFileStatuses(
    // PRD-1544: carry `retry` through so the per-file retry controls can read it.
    datasetFileList.map((f) => ({ file_id: f.file_id || f.id, file_name: f.file_name, status: f.status, retry: f.retry })),
    pipelineJobs,
  ), [pipelineJobs, datasetFileList]);

  // PRD-1544: submit a retry for one or many failed files. Optimistically mark them
  // Processing, then reconcile with the 202 (accepted stay Processing; rejected revert
  // and show their reason inline). The backend dispatches — we do NOT call /route;
  // a refetch + the parent's poll converge the real statuses.
  const submitRetry = useCallback(async (fileIds) => {
    if (!canRetry || !fileIds || fileIds.length === 0) return;
    setRetryError(null);
    setRetryRejections((prev) => {
      const next = { ...prev };
      fileIds.forEach((id) => delete next[id]);
      return next;
    });
    setRetryingIds((prev) => {
      const next = new Map(prev);
      fileIds.forEach((id) => {
        const file = allFiles.find((f) => f.file_id === id);
        next.set(id, Number(file?.retry?.attempts_used) || 0);
      });
      return next;
    });
    try {
      const resp = await apiService.retryDatasetFiles(dataset.id, fileIds);
      const rejected = resp?.rejected || [];
      const rejectedIds = rejected.map((r) => r.file_id);
      setRetryingIds((prev) => {
        const next = new Map(prev);
        rejectedIds.forEach((id) => next.delete(id));
        return next;
      });
      if (rejected.length) {
        setRetryRejections((prev) => {
          const next = { ...prev };
          rejected.forEach((r) => { next[r.file_id] = r.reason_code; });
          return next;
        });
      }
      fetchPipelineJobs({ quiet: true });
    } catch (err) {
      setRetryingIds((prev) => {
        const next = new Map(prev);
        fileIds.forEach((id) => next.delete(id));
        return next;
      });
      const status = err?.status;
      setRetryError(
        status === 409 ? 'This dataset is paused or being updated — try again shortly.'
          : status === 422 ? 'No files are eligible to retry.'
            : status === 403 ? 'You don’t have permission to retry files in this dataset.'
              : 'Could not retry right now. Please try again.',
      );
    }
  }, [canRetry, dataset.id, fetchPipelineJobs, allFiles]);

  // A submitted retry shows "Retrying…" until the server moves on: the file leaves
  // `failed` (its real status takes over), or comes back failed with another attempt
  // counted — a re-fail, which then renders with its Retry / Re-upload controls.
  const isPendingRetry = (file) => retryingIds.has(file.file_id)
    && file.status === 'failed'
    && (Number(file.retry?.attempts_used) || 0) <= retryingIds.get(file.file_id);

  // Shared File Status count line. `attention` is either a true failed count
  // (attentionIsFailed=true → "N failed", red) or the lifecycle rollup's
  // needs_attention bucket (attentionIsFailed=false → "N need attention", amber).
  // The two are NOT the same: needs_attention also covers files awaiting
  // triage, so labelling it "failed" would alarm the user. This matches
  // getDatasetCountLine's v13 wording in utils/datasetBadge.js.
  //
  // `awaitingReview` is a separate fourth bucket (document field review) and is
  // NOT inside needs_attention, so it needs its own term or the line silently
  // under-reports: ready + processing + awaiting_review + needs_attention = total.
  //
  // `rejected` (PRD-1628) is only passed where `attention` is a true failed count;
  // the needs_attention bucket already includes rejected files.
  const renderCountLabel = (complete, total, attention, processing, attentionIsFailed, awaitingReview = 0, rejected = 0) => (
    // Figma 1017-2637 spells this line "1 of 3 files complete • 2 require review":
    // "of" not "/", a bullet (U+2022) not a middot, and "require review" not
    // "need review". The card's own count line keeps "/" and "·" — frame
    // 1016-2544 draws it that way — so the two surfaces differ on purpose.
    <span className="dataset-details-pipeline-label">
      {complete} of {total} files complete
      {attention > 0 && (
        attentionIsFailed
          ? <span style={{ color: 'var(--color-typography-text-error)' }}> • {attention} failed</span>
          : <span> • {attention} need attention</span>
      )}
      {rejected > 0 && <span style={{ color: 'var(--color-typography-text-error)' }}> • {rejected} rejected</span>}
      {awaitingReview > 0 && <span> • {awaitingReview} {requireVerb(awaitingReview)} review</span>}
      {processing > 0 && <span> • {processing} processing</span>}
    </span>
  );

  const renderFileStatus = () => {
    if (pipelineJobsLoading) return <p className="dataset-details-pipeline-loading">Loading file status...</p>;

    // Fallback: if /jobs hasn't surfaced per-file info yet (job created but result JSON
    // is still bare, e.g. immediately after a fresh dataset is created), use the
    // dataset-level file_summary so the modal matches the card instead of showing nothing.
    if (allFiles.length === 0) {
      // Prefer the lifecycle rollup (file_counts); fall back to the legacy
      // DatasetFile summary only when there's no lifecycle row (PRD-1119).
      // file_counts.needs_attention is the "need attention" bucket (triage
      // + failed), NOT a pure failed count; file_summary.failed IS a true failed
      // count — hence the attentionIsFailed distinction. Files parked for
      // document field review live in their own awaiting_review bucket.
      const fc = dataset.file_counts;
      let complete, attention, processing, total, attentionIsFailed, awaitingReview, rejected = 0;
      if (fc && fc.total > 0) {
        ({ ready: complete, needs_attention: attention, processing } = fc);
        total = fc.total;
        awaitingReview = fc.awaiting_review ?? 0;
        attentionIsFailed = false;
      } else if (dataset.file_summary && dataset.file_summary.total) {
        ({ complete, failed: attention, processing } = dataset.file_summary);
        rejected = dataset.file_summary.rejected ?? 0;
        total = dataset.file_summary.total;
        // The legacy summary predates the review bucket and has no equivalent.
        awaitingReview = 0;
        attentionIsFailed = true;
      } else {
        return null;
      }
      return (
        <div className="dataset-details-section">
          <h3 className="dataset-details-section-title">File Status</h3>
          <div className="dataset-details-pipeline-list">
            <div className="dataset-details-pipeline-item">
              <div className="dataset-details-pipeline-row">
                {renderCountLabel(complete, total, attention, processing, attentionIsFailed, awaitingReview, rejected)}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // The pipeline lifecycle rollup (file_counts) is the correct source for a
    // file's real outcome — DatasetFile can lag (e.g. shows 'complete' for a file
    // that actually failed). Prefer it for the summary so the modal matches the
    // card; fall back to the per-file tally only when there's no lifecycle rollup
    // (PRD-1119).
    const fc = dataset.file_counts;
    const hasFc = !!(fc && fc.total > 0);
    const succeeded = hasFc ? fc.ready : allFiles.filter(f => f.status === 'success' || f.status === 'complete').length;
    // From file_counts this is the needs_attention bucket ("need attention"),
    // rejected files included; from the per-file tally it's a true failed count
    // ("failed"), with rejected files counted apart.
    const attention = hasFc ? fc.needs_attention : allFiles.filter(f => f.status === 'failed').length;
    const rejected = hasFc ? 0 : allFiles.filter(f => f.status === 'rejected').length;
    const attentionIsFailed = !hasFc;
    // Document field review is its own bucket, so it must also come out of the
    // per-file fallback's catch-all "processing" tally or it double-counts.
    // awaiting_input (CSV) deliberately stays inside that tally — changing it
    // would alter the existing CSV review line.
    const awaitingReview = hasFc
      ? (fc.awaiting_review ?? 0)
      : allFiles.filter(f => f.status === 'awaiting_review').length;
    const processing = hasFc ? fc.processing : allFiles.filter(f => !['success', 'complete', 'failed', 'rejected', 'awaiting_review'].includes(f.status)).length;
    const total = hasFc ? fc.total : allFiles.length;

    // PRD-1544: failed files the owner can retry right now (eligible + attempts left,
    // and not already mid-retry). Drives the "Retry all" action.
    const eligibleFailedIds = canRetry
      ? allFiles
        .filter((f) => f.status === 'failed' && canRetryFile(f.retry) && !isPendingRetry(f))
        .map((f) => f.file_id)
      : [];

    // Failed and rejected files are lifted out of the collapsed list into an always-open box,
    // so the ones that need a decision are seen without expanding anything; the
    // rest stay folded behind the count line. A file mid-retry is still `failed`,
    // so it stays in the box showing "Retrying…". A failed file with no `retry`
    // info (the backend omits it while it can't reach the pipeline) stays in the
    // box too, with a line saying its options are unavailable for now. With retry
    // off there is nothing to act on, and the panel is unchanged.
    const attentionFiles = canRetry ? allFiles.filter(needsAction) : [];
    const listedFiles = canRetry ? allFiles.filter((f) => !needsAction(f)) : allFiles;
    // The box already names its files, so the fold's count line leaves them out
    // rather than repeat "N need attention" under it. Anything else in the
    // attention bucket (e.g. files awaiting triage) is still counted.
    const boxedRejected = attentionFiles.filter(f => f.status === 'rejected').length;
    // needs_attention counts the boxed rejected files; the per-file failed count doesn't.
    const foldAttention = Math.max(0, attention - attentionFiles.length + (hasFc ? 0 : boxedRejected));
    const foldRejected = Math.max(0, rejected - boxedRejected);

    const renderFileRow = (file, idx) => {
      const isRetrying = isPendingRetry(file);
      const fileIcon = isRetrying
        ? STATUS_ICON.processing
        : (STATUS_ICON[file.status] || STATUS_ICON.processing);
      // A rejected file's way forward is known without asking: a valid copy.
      const retry = file.status === 'rejected' ? (file.retry || UPLOAD_REJECTED_RETRY) : file.retry;
      // Retry affordance only for the owner, on a failed or rejected file, with the flag on.
      const showReason = canRetry && !isRetrying && needsAction(file) && !!retry;
      const rejection = retryRejections[file.file_id];
      return (
        <div key={file.file_id || idx} className="dataset-details-pipeline-file-row">
          <div className="dataset-details-pipeline-file-main">
            <Icon name={fileIcon.icon} className="dataset-details-pipeline-file-icon" size={14} style={{ color: fileIcon.color }} />
            <span className="dataset-details-pipeline-file-id">{file.file_name || (file.file_id ? file.file_id.slice(0, 8) + '...' : 'N/A')}</span>
            {isRetrying && <span className="dataset-details-retry-note">Retrying…</span>}
            {file.status === 'rejected' && <span className="dataset-details-retry-note">Rejected</span>}
            {showReason && (
              <div className="dataset-details-retry-actions">
                {canRetryFile(retry) && (
                  <button
                    type="button"
                    className="resui-btn resui-btn--secondary resui-btn--small"
                    onClick={() => submitRetry([file.file_id])}
                  >
                    Retry
                  </button>
                )}
                {/* Re-upload is always offered on a failed file (alongside
                    Retry when eligible); for replace / retry-limit-reached it
                    is the only option. Opens the dataset edit/upload flow. */}
                {onEdit && (
                  <button
                    type="button"
                    className="resui-btn resui-btn--secondary resui-btn--small"
                    onClick={() => { onClose(); onEdit(dataset); }}
                  >
                    Re-upload
                  </button>
                )}
              </div>
            )}
          </div>
          {showReason && (
            <span className="dataset-details-retry-reason">{retryStatusText(retry)}</span>
          )}
          {canRetry && !isRetrying && needsAction(file) && !retry && (
            <span className="dataset-details-retry-reason">Retry options are unavailable right now. Check back shortly.</span>
          )}
          {rejection && (
            <span className="dataset-details-retry-reason dataset-details-retry-reason--error">{rejectionText(rejection)}</span>
          )}
        </div>
      );
    };

    return (
      <div className="dataset-details-section">
        <h3 className="dataset-details-section-title">File Status</h3>
        {retryError && (
          <p className="dataset-details-retry-error" role="alert">{retryError}</p>
        )}
        <div className="dataset-details-pipeline-list">
          {attentionFiles.length > 0 && (
            <div className="dataset-details-pipeline-item dataset-details-attention">
              <div className="dataset-details-section-head">
                <span className="dataset-details-pipeline-label">
                  {attentionFiles.length} {attentionFiles.length === 1 ? 'file needs' : 'files need'} attention
                </span>
                {eligibleFailedIds.length > 0 && (
                  <button
                    type="button"
                    className="resui-btn resui-btn--secondary resui-btn--small dataset-details-retry-all"
                    onClick={() => submitRetry(eligibleFailedIds)}
                  >
                    Retry all retryable ({eligibleFailedIds.length})
                  </button>
                )}
              </div>
              <div className="dataset-details-pipeline-files">
                {attentionFiles.map(renderFileRow)}
              </div>
            </div>
          )}
          <div className="dataset-details-pipeline-item">
            {/* Nothing left to fold when every file sits in the box above, so the
                count line is plain text rather than a toggle that adds no rows. */}
            {listedFiles.length > 0 ? (
              <div
                className="dataset-details-pipeline-row"
                onClick={() => setFileStatusExpanded(prev => !prev)}
                style={{ cursor: 'pointer' }}
              >
                {renderCountLabel(succeeded, total, foldAttention, processing, attentionIsFailed, awaitingReview, foldRejected)}
                <Icon name={fileStatusExpanded ? 'expand_less' : 'expand_more'} className="dataset-details-pipeline-expand" size={18} />
              </div>
            ) : (
              <div className="dataset-details-pipeline-row">
                {renderCountLabel(succeeded, total, foldAttention, processing, attentionIsFailed, awaitingReview, foldRejected)}
              </div>
            )}
            {fileStatusExpanded && listedFiles.length > 0 && (
              <div className="dataset-details-pipeline-files">
                {listedFiles.map(renderFileRow)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const fetchSampleData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getLatestExtractionResult(dataset.id);
      console.log('API Response:', result);

      // Extract extracted_data from result.results[].extracted_data
      // The structure is: result.results = [{ extracted_data: {...} } }]
      let extractedDataArray = [];

      // Per-file map for the reviewer media view (R5) — preserves file_id, which
      // the answer-only flattening below intentionally drops.
      const byFile = {};

      if (result?.result?.results && Array.isArray(result.result.results)) {
        // Process each result in the results array — only keep the answer field
        result.result.results.forEach((item) => {
          const ed = item?.extracted_data ?? item?.data?.extracted_data;
          const fid = item?.file_id ?? item?.data?.file_id ?? ed?.file_id;
          if (ed?.answer !== undefined) {
            extractedDataArray.push({ answer: ed.answer });
            if (fid) byFile[fid] = { answer: ed.answer };
          } else if (ed) {
            extractedDataArray.push(ed);
            if (fid) byFile[fid] = ed;
          }
        });
      }

      setResultsByFile(byFile);

      // Fallback: if no results array, try direct extracted_data (for backward compatibility)
      if (extractedDataArray.length === 0 && result?.result?.extracted_data) {
        const extractedData = result.result.extracted_data;
        // If it's an array, use it directly
        if (Array.isArray(extractedData)) {
          extractedDataArray = extractedData;
        } else if (typeof extractedData === 'object') {
          // If it's an object, convert to array with one row
          extractedDataArray = [extractedData];
        }
      }

      console.log('Processed extracted data:', extractedDataArray);

      // Check if we have empty results or no data
      if (extractedDataArray.length === 0) {
        // No data found - treat as empty state
        setError(null);
        setSampleData([]);
      } else {
        setSampleData(extractedDataArray);
      }

      // Always mark as fetched to prevent re-fetching
      hasFetchedRef.current = true;
    } catch (err) {
      console.error('Error fetching sample data:', err);

      // Check if it's a 404 error with the specific message about no completed extraction job
      if (err.status === 404 ||
          (err.message && err.message.includes('No completed extraction job found'))) {
        // Set error to null so it shows the empty state instead
        setError(null);
        setSampleData([]);
      } else {
        setError(err.message || 'Failed to fetch sample data');
      }

      // Always mark as fetched even on error to prevent infinite retries
      hasFetchedRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [dataset.id]);

  // Reset fetch flag when dataset changes
  useEffect(() => {
    hasFetchedRef.current = false;
    setSampleData(null);
    setError(null);
    setLoading(false);
  }, [dataset.id]);

  // Fetch sample data when modal opens (only if AI summary flag is on)
  useEffect(() => {
    if (showAiIndexedSummary && !hasFetchedRef.current && !loading) {
      fetchSampleData();
    }
  }, [showAiIndexedSummary, fetchSampleData]);

  // Get column names from sample data
  const columns = useMemo(() => {
    if (!sampleData || sampleData.length === 0) return [];
    const allKeys = new Set();
    sampleData.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });
    return Array.from(allKeys);
  }, [sampleData]);

  // Reviewer media view (R5) applies only when the dataset has image captures.
  const hasImageFiles = useMemo(
    () => allFiles.some((f) => /\.(png|jpe?g|webp|gif)$/i.test(f.file_name || '')),
    [allFiles]
  );

  const toggleRow = (rowIndex) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
    }
    setExpandedRows(newExpanded);
  };

  const renderSampleDataSection = () => {
    // R5: when enabled and the dataset has image captures, show the image beside
    // the extracted fields instead of the flat AI-summary table.
    if (reviewerMediaEnabled && hasImageFiles) {
      return (
        <div className="dataset-details-sample-data-section">
          <ReviewMediaPanel datasetId={dataset.id} files={allFiles} resultsByFile={resultsByFile} />
        </div>
      );
    }

    if (loading) {
      return (
        <div className="dataset-details-loading">
          <p>Loading sample data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="dataset-details-error">
          <p>Error: {error}</p>
          <button onClick={fetchSampleData} className="dataset-details-retry-button">
            Retry
          </button>
        </div>
      );
    }

    if (!sampleData || sampleData.length === 0) {
      return (
        <div className="dataset-details-empty">
          <p>No completed extraction jobs found for this dataset.</p>
        </div>
      );
    }

    const columnDisplayName = (col) => (col === 'answer' ? 'AI Indexed Summary' : col);

    return (
      <div className="dataset-details-sample-data-section">
          <div className="dataset-details-table-container">
            <table className="dataset-details-sample-table">
              <thead>
                <tr>
                  <th className="dataset-details-sample-table-header dataset-details-expand-header" style={{ width: '40px' }}></th>
                  {columns.map((column, index) => (
                      <th key={index} className="dataset-details-sample-table-header">
                        {columnDisplayName(column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sampleData.map((row, rowIndex) => {
                    const isExpanded = expandedRows.has(rowIndex);
                    return (
                      <React.Fragment key={rowIndex}>
                        <tr className="dataset-details-sample-table-row">
                          <td className="dataset-details-expand-cell">
                            <button
                              className="dataset-details-expand-button"
                              onClick={() => toggleRow(rowIndex)}
                              title={isExpanded ? 'Collapse row' : 'Expand row'}
                            >
                              <Icon name={isExpanded ? 'expand_less' : 'expand_more'} />
                            </button>
                          </td>
                          {columns.map((column, colIndex) => {
                            const cellValue = row[column] !== null && row[column] !== undefined ? String(row[column]) : '-';
                            return (
                              <td key={colIndex} className="dataset-details-sample-table-cell" title={cellValue !== '-' ? cellValue : ''}>
                                <div className="dataset-details-cell-content">
                                  {cellValue}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                        {isExpanded && (
                          <tr className="dataset-details-sample-table-row-expanded">
                            <td colSpan={columns.length + 1} className="dataset-details-expanded-content">
                              <div className="dataset-details-expanded-details">
                                {columns.map((column) => {
                                  const cellValue = row[column] !== null && row[column] !== undefined ? String(row[column]) : '-';
                                  return (
                                    <div key={column} className="dataset-details-expanded-field">
                                      <span className="dataset-details-expanded-label">{columnDisplayName(column)}:</span>
                                      <span className="dataset-details-expanded-value">{cellValue}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
        </div>
    );
  };

  return (
    <>
      <div className="dataset-details-backdrop" onClick={onClose} />
      <div className="dataset-details-modal">
        <div className="dataset-details-page">
        <div className="dataset-details-header">
          <h1 className="dataset-details-title">{dataset.name}</h1>
          <div className="dataset-details-header-actions">
            {/* PRD-1359 (feedback doc §Dataset Modal): the loose edit and delete
                icons collapse into one overflow menu beside the close icon, and
                it renders the SAME action list as the card menu — the doc asks
                for them to match, so they share one component rather than two
                copies that drift.
                Each action closes this modal first: they open a drawer or a
                confirm of their own, and the doc is explicit that review and
                its neighbours must not be layered on another modal. */}
            {/* Gated exactly as the card's menu is: the shell must not mount when
                the list would be empty or the viewer does not own the dataset.
                See canShowDatasetMenu for why each half matters. */}
            {canShowDatasetMenu(dataset, features) && (
            <RowActionsMenu ariaLabel="Dataset actions" menuClassName="dataset-details-actions-menu">
              {(close) => (
                <DatasetMenuItems
                  dataset={dataset}
                  features={features}
                  close={close}
                  onReviewFields={onOpenDocumentReview && ((ds) => onOpenDocumentReview(ds))}
                  onToggleShare={onToggleShare && ((ds) => { onClose(); onToggleShare(ds); })}
                  onEdit={onEdit && ((ds) => { onClose(); onEdit(ds); })}
                  onTogglePause={onTogglePause && ((ds) => { onClose(); onTogglePause(ds); })}
                  onDelete={onDelete && ((ds) => { onClose(); onDelete(ds); })}
                  deleteClassName="dataset-details-menu-delete"
                />
              )}
            </RowActionsMenu>
            )}
            <button className="dataset-details-close" onClick={onClose}>
              <Icon name="close" />
            </button>
          </div>
        </div>

        <div className="dataset-details-content">
          <div className="dataset-details-section">
            {/* Figma 1017-2637 renders this "Dataset details", not title case. */}
            <h3 className="dataset-details-section-title">Dataset details</h3>
            {/* PRD-1359: alert banner between the section title and the info rows,
                per the frame — it explains the Review required status the Status row
                below is about to show, so it has to be read first. Copy is verbatim
                from the feedback doc. */}
            {isAwaitingReviewOnly(dataset) && (
              <div className="resui-banner resui-banner--alert dataset-details-review-banner">
                <Icon name="info" />
                <span className="resui-banner-text">
                  Review required for {dataset.file_counts?.awaiting_review ?? 0} document
                  {(dataset.file_counts?.awaiting_review ?? 0) === 1 ? '' : 's'}.
                  {' '}Verify or correct the fields on each to finish processing.
                </span>
              </div>
            )}
            <div className="dataset-details-info">
              <div className="dataset-details-info-row">
                <span className="dataset-details-label">Name:</span>
                <span className="dataset-details-value">{dataset.name}</span>
              </div>
              <div className="dataset-details-info-row">
                <span className="dataset-details-label">Owner:</span>
                <span className="dataset-details-value">{dataset.created_by_name || 'N/A'}</span>
              </div>
              <div className="dataset-details-info-row">
                <span className="dataset-details-label">Status:</span>
                <Chip variant={getDisplayStatusVariant(dataset)} className="dataset-details-badge">
                  {getDisplayStatus(dataset)}
                </Chip>
              </div>
              <div className="dataset-details-info-row">
                <span className="dataset-details-label">Last Updated:</span>
                <span className="dataset-details-value">
                  <Timestamp value={dataset.last_activity_at || dataset.updated_at} form="long" />
                </span>
              </div>
            </div>
            {enableCsvHitlReview && dataset.pipeline_summary?.awaiting_input > 0 && onOpenReview && !reviewSubmitted && (
              <button
                className="data-sources-review-button"
                onClick={() => onOpenReview(dataset)}
                style={{ marginTop: '16px' }}
              >
                <Icon name="rate_review" />
                Review
              </button>
            )}
            {enableCsvHitlReview && dataset.pipeline_summary?.awaiting_input > 0 && reviewSubmitted && (
              <span className="data-sources-review-submitted" style={{ marginTop: '16px' }}>
                <Icon name="check_circle" />
                Review submitted — processing
              </span>
            )}
          </div>
          {renderFileStatus()}
          {/* Figma 1017-2637: "Review fields" sits BELOW the files panel, not above
              it — the reviewer reads which files are parked, then acts. Primary
              button, no icon (the frame's Button - Primary, 622-1820). */}
          {onOpenDocumentReview && isAwaitingReviewOnly(dataset) && (
            <button
              type="button"
              className="resui-btn resui-btn--primary dataset-details-review-fields-btn"
              onClick={() => onOpenDocumentReview(dataset)}
            >
              Review fields
            </button>
          )}
          {showAiIndexedSummary && renderSampleDataSection()}
        </div>
        </div>
      </div>
    </>
  );
};

export default DatasetDetails;
